import { createListCollection } from "@ark-ui/react";
import { Alert } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import classnames from "classnames";
import { Eye } from "lucide-react";

import i18n, { useTranslation } from "@/lib/i18n";
import { useExtranetLocale } from "@/lib/locale/use-extranet-locale";
import {
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationSeriesInner as ParameterTimeSeriesSeries,
} from "@/lib/api";
import { formatUnitLabel } from "@/lib/api/parameter-time-series-configuration.utils";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { propertyKindLabel } from "@/lib/parameters/property-kind-label";
import Button from "@/components/button/button";
import Icon, { type IconNames } from "@/components/icon/icon";
import Stack from "@/components/stack/stack";
import Select from "@/components/select/select";
import useMediaQuery from "@/features/shared/hooks/use-media-query";
import {
  coerceVisualizationType,
  getAllowedVisualizationTypes,
  readVisualizationType,
  seriesParameterIds,
  visualizationSupportsThresholds,
  type ModelMapVisualizationType,
} from "@/features/shared/widgets/model-map/model-map-visualization-types";
import {
  createEmptyTimeSeriesConfiguratorDraft,
  finalizeTimeSeriesConfiguration,
  isTimeSeriesDraftReadyToSave,
  resolveDraftUnit,
  resolveOverlappingUnitsForSeries,
  resolvePickListExclusion,
  resolvePropertyKindFallbackTitle,
  resolveSeriesParameters,
  toPreviewTimeSeriesConfiguration,
  toTimeSeriesConfiguratorDraft,
  type TimeSeriesConfiguratorDraft,
} from "@/features/widget-forms/forms/weather-model-map/time-series-configurator.utils";
import { TimeSeriesThresholdsEditor } from "@/features/widget-forms/forms/weather-model-map/components/time-series-thresholds-editor";
import { SortableItem, SortableList } from "@/features/admin/components/sortable-list";
import TimeSeriesSeriesCard from "@/features/widget-forms/forms/weather-model-map/components/time-series-series-card";
import {
  MapWidgetEditableTitle,
  type MapWidgetEditableTitleStrings,
} from "@/features/widget-forms/forms/map-widget-editor/map-widget-editable-title";
import { StatusLabel } from "@/features/widget-forms/forms/weather-model-map/components/check-card/check-card";
import {
  PickListItem,
  PickListModal,
} from "@/features/widget-forms/forms/weather-model-map/modals/pick-list-modal";
import modalStyles from "@/features/widget-forms/forms/weather-model-map/modals/modal-styles.module.css";
import PreviewModal from "@/features/widget-forms/forms/weather-model-map/modals/preview-modal";
import { toMoved } from "@/lib/array";

import { ConfigureSeriesModal, SeriesDetails } from "../modals/configure-series-modal";

import styles from "./time-series-configurator-body.module.css";

const BodyTab = {
  /** Combined "Tijdreeks" tab: general settings first, then the parameter list. */
  TimeSeries: "time-series",
  Thresholds: "thresholds",
} as const;
type BodyTab = (typeof BodyTab)[keyof typeof BodyTab];

/**
 * At/above this viewport width the preview sits beside the form (desktop). Below
 * it (mobile) the preview column is hidden and reached via a "voorbeeld" button
 * that opens the fullscreen preview modal — mirroring the map editor dialogs.
 */
const PREVIEW_DESKTOP_MEDIA = "(min-width: 1200px)";

export interface TimeSeriesConfiguratorBodyProps {
  initialConfig: ParameterTimeSeriesConfiguration | TimeSeriesConfiguratorDraft;
  parameters: TimeSeriesParameterOption[];
  /** Dataset filter options for the picker; includes models with no parameters yet. */
  datasetLabels?: string[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  configuratorIndex: number | null;
  parametersLoading?: boolean;
  /** Property kinds failed to load, so no units can be offered. */
  unitsError?: boolean;
  /**
   * Returning a promise (the widget dialog, which persists) keeps the footer
   * confirm button in its saving state until the request settles.
   */
  onDone: (config: ParameterTimeSeriesConfiguration) => void | Promise<void>;
  onCancel: () => void;
  /** Overrides the footer cancel label. */
  cancelLabel?: string;
  /** Overrides the footer confirm label (default: Done). */
  doneLabel?: string;
  /** Shows a left-pointing back arrow in the header title that triggers onCancel. */
  showBackButton?: boolean;
  /**
   * Header owns the title instead of showing a static heading (used by the
   * dialog variants). A new chart opens with an auto-focused required input;
   * once it has a name, the header shows it as click-to-edit. The General
   * section then omits its own title input to avoid duplication, and an empty
   * title blocks saving rather than falling back to the untitled label.
   */
  editableTitle?: boolean;
  /**
   * Optional widget preview. When provided, the header and footer still span the
   * full width while the preview sits beside the scrolling content only, so the
   * dialog chrome stays visually predictable.
   */
  previewSlot?: ReactNode;
  /**
   * Optional live preview driven by the in-progress draft. Receives the draft as
   * a best-effort {@link ParameterTimeSeriesConfiguration} (or `null` until it
   * has a series and unit). Takes precedence over {@link previewSlot} and is laid
   * out in the same preview column, so callers can render e.g. a chart of the
   * configuration currently being edited. Reflects live edits.
   */
  renderPreview?: (
    liveConfig: ParameterTimeSeriesConfiguration | null,
  ) => ConfiguratorPreviewRender;
  className?: string;
}

/**
 * Describes a live preview split into a compositional {@link toolbar} (rendered
 * in the preview header, both inline and in the maximize dialog) and a
 * {@link content} body. {@link wrap} establishes the shared runtime (a
 * `WidgetContextHost` plus any preview-local state provider) around both halves,
 * so the header controls drive the previewed body even across the dialog portal.
 */
export type ConfiguratorPreviewRender = {
  wrap?: (node: ReactNode) => ReactNode;
  toolbar?: ReactNode;
  content: ReactNode;
};

function syncDraftUnit(
  draft: TimeSeriesConfiguratorDraft,
  parameters: TimeSeriesParameterOption[],
): TimeSeriesConfiguratorDraft {
  const overlappingUnits = resolveOverlappingUnitsForSeries(draft, parameters);
  const unit = resolveDraftUnit({
    hasSeries: draft.series.length > 0,
    overlappingUnits,
    currentUnit: draft.unit,
  });
  return { ...draft, unit };
}

function coerceDraftVisualization(
  draft: TimeSeriesConfiguratorDraft | ParameterTimeSeriesConfiguration,
): ModelMapVisualizationType {
  return coerceVisualizationType(
    readVisualizationType(draft as ParameterTimeSeriesConfiguration),
    draft.unit?.code,
    seriesParameterIds(draft),
  );
}

function initDraft(
  initialConfig: ParameterTimeSeriesConfiguration | TimeSeriesConfiguratorDraft,
  parameters: TimeSeriesParameterOption[],
): TimeSeriesConfiguratorDraft {
  const draft =
    "unit" in initialConfig && initialConfig.series.length === 0 && !initialConfig.unit
      ? createEmptyTimeSeriesConfiguratorDraft()
      : toTimeSeriesConfiguratorDraft(initialConfig as ParameterTimeSeriesConfiguration);
  // Only resolve the unit against the parameter list once it is known. If the
  // dialog opens before parameters have loaded, keep the draft's own unit so a
  // reopened group shows its unit instead of it being wiped (it is re-synced by
  // an effect once parameters arrive).
  const synced = parameters.length > 0 ? syncDraftUnit(draft, parameters) : draft;
  return {
    ...synced,
    visualization_type: coerceDraftVisualization(synced),
  };
}

/**
 * Container-agnostic time-series configurator. Renders header + content +
 * footer with no dialog/grid assumptions, so it can be embedded in a dialog or
 * a preview column. The content is laid out as a paneled tab rail (general +
 * parameters, thresholds) with a horizontal rail. All draft state lives here;
 * callers remount it per edit session.
 */
export function TimeSeriesConfiguratorBody({
  initialConfig,
  parameters,
  datasetLabels,
  timeSeriesConfigurations,
  configuratorIndex,
  parametersLoading = false,
  unitsError = false,
  onDone,
  onCancel,
  cancelLabel,
  doneLabel,
  showBackButton = false,
  editableTitle = false,
  previewSlot,
  renderPreview,
  className,
}: TimeSeriesConfiguratorBodyProps) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TimeSeriesConfiguratorDraft>(() =>
    initDraft(initialConfig, parameters),
  );
  const [saving, setSaving] = useState(false);
  const [showConfigureSeries, setShowConfigureSeries] = useState(false);
  const [selectedParameterId, setSelectedParameterId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BodyTab>(BodyTab.TimeSeries);

  // Preview sits beside the form on desktop; on mobile it is hidden and opened
  // fullscreen from a header button instead.
  const isPreviewDesktop = useMediaQuery(PREVIEW_DESKTOP_MEDIA);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // The dialog can open before the parameter list has loaded. Once it arrives,
  // resolve the draft unit against the now-known units: this preserves the
  // group's saved unit (same code) and only fills/normalizes it, so the unit
  // dropdown becomes correct as soon as the data is fetched. Guarded so it does
  // not churn state when the unit is already resolved.
  useEffect(() => {
    if (parameters.length === 0) return;
    setConfig((previous) => {
      const synced = syncDraftUnit(previous, parameters);
      if (synced.unit?.code === previous.unit?.code) return previous;
      return {
        ...synced,
        visualization_type: coerceDraftVisualization(synced),
      };
    });
  }, [parameters]);

  const selectedParameterIds = useMemo(
    () => config.series.map((s) => s.parameter_id),
    [config.series],
  );

  const overlappingUnits = useMemo(
    () => resolveOverlappingUnitsForSeries(config, parameters),
    [config, parameters],
  );

  const selectedUnitCode: string | undefined = config.unit?.code;
  const series = config.series;

  const usedInOtherGroups = useMemo(() => {
    const map = new Map<string, { groupTitle: string; label: string }>();
    const untitled = t("admin.widgets.fields.model_map.general.untitled");
    for (let i = 0; i < timeSeriesConfigurations.length; i++) {
      if (i === configuratorIndex) continue;
      const cfg = timeSeriesConfigurations[i];
      const groupTitle = cfg.title?.trim() || untitled;
      for (const s of cfg.series ?? []) {
        map.set(s.parameter_id, { groupTitle, label: s.label });
      }
    }
    return map;
  }, [timeSeriesConfigurations, configuratorIndex, t]);

  const allowedPresentationTypes = useMemo(
    () => getAllowedVisualizationTypes(selectedUnitCode, selectedParameterIds),
    [selectedUnitCode, selectedParameterIds],
  );

  const presentationCollection = useMemo(
    () =>
      createListCollection({
        items: allowedPresentationTypes.map((v) => ({
          label: t(`admin.widgets.fields.model_map.time_series.visualization_${v}`),
          value: v,
        })),
      }),
    [allowedPresentationTypes, t],
  );

  const unitCollection = useMemo(
    () =>
      createListCollection({
        items: overlappingUnits.map((unit) => ({
          label: formatUnitLabel(unit),
          value: unit.code,
        })),
      }),
    [overlappingUnits],
  );

  const presentationValue = coerceDraftVisualization(config);

  const pickListItems = useMemo((): PickListItem[] => {
    const unitExcludedLabel: StatusLabel = {
      text: t("admin.widgets.fields.model_map.parameters.unit_excluded"),
      icon: "x",
    };
    const propertyKindExcludedLabel: StatusLabel = {
      text: t("admin.widgets.fields.model_map.parameters.property_kind_excluded"),
      icon: "x",
    };
    const seriesParameters = resolveSeriesParameters({ series }, parameters);
    // Only a non-empty group has a meaningful unit; never filter on a leftover one.
    const groupUnitCode = seriesParameters.length > 0 ? selectedUnitCode : undefined;
    return parameters.map((p) => {
      const inOtherGroup = usedInOtherGroups.get(p.id);
      const disabledWithLabel = inOtherGroup
        ? {
            text: t("admin.widgets.fields.model_map.parameters.already_in_group", {
              group: inOtherGroup.groupTitle,
              label: inOtherGroup.label,
            }),
            icon: "check" as const,
          }
        : undefined;
      const exclusion = resolvePickListExclusion(p, seriesParameters, groupUnitCode);
      const excluded =
        exclusion === "property_kind"
          ? propertyKindExcludedLabel
          : exclusion === "unit"
            ? unitExcludedLabel
            : undefined;
      return {
        id: p.id,
        title: p.label,
        subtitle: p.datasetLabel,
        // Kind is no longer shown, but stays searchable (e.g. "temperatuur").
        searchText: propertyKindLabel(t, p.propertyKindId),
        datasetLabel: p.datasetLabel,
        disabledWithLabel,
        excluded,
      };
    });
  }, [parameters, series, selectedUnitCode, usedInOtherGroups, t]);

  const thresholdsAvailable = config.series.length > 0 && !!config.unit?.code;

  // An empty title falls back to the group's property kind label ("Luchtdruk").
  // Saved titles are read by dashboard viewers, so translate into the extranet's
  // language rather than the admin UI language (always nl).
  const extranetLocale = useExtranetLocale();
  const contentT = useMemo(() => i18n.getFixedT(extranetLocale), [extranetLocale]);
  const propertyKindTitle = useMemo(
    () =>
      resolvePropertyKindFallbackTitle(
        resolveSeriesParameters({ series }, parameters),
        (id) => propertyKindLabel(contentT, id),
      ),
    [series, parameters, contentT],
  );
  const untitledLabel = t("admin.widgets.fields.model_map.general.untitled");

  // When the header owns the title it is a required field, so a blank one
  // blocks saving unless the property kind label can stand in. Without it the
  // title stays optional (see handleDone).
  const canDone = isTimeSeriesDraftReadyToSave(config, {
    requireTitle: editableTitle,
    fallbackTitle: propertyKindTitle,
  });

  const updateDraft = useCallback(
    (updater: (previous: TimeSeriesConfiguratorDraft) => TimeSeriesConfiguratorDraft) => {
      setConfig((previous) => {
        const next = syncDraftUnit(updater(previous), parameters);
        return {
          ...next,
          visualization_type: coerceDraftVisualization(next),
        };
      });
    },
    [parameters],
  );

  const handleAddParameter = useCallback((parameterId: string) => {
    setSelectedParameterId(parameterId);
    setShowConfigureSeries(true);
  }, []);

  const handleConfigureSeriesSubmit = useCallback(
    (details: SeriesDetails) => {
      if (!selectedParameterId) return;
      const newSeries: ParameterTimeSeriesSeries = {
        parameter_id: selectedParameterId,
        label: details.label.trim(),
      };
      updateDraft((previous) => ({
        ...previous,
        series: [...previous.series, newSeries],
      }));
      setShowConfigureSeries(false);
      setSelectedParameterId(null);
    },
    [selectedParameterId, updateDraft],
  );

  const handleConfigureSeriesCancel = useCallback(() => {
    setShowConfigureSeries(false);
    setSelectedParameterId(null);
  }, []);

  const handleRemoveSeries = useCallback(
    (parameterId: string) => {
      updateDraft((previous) => ({
        ...previous,
        series: previous.series.filter((s) => s.parameter_id !== parameterId),
      }));
    },
    [updateDraft],
  );

  const handleReorderSeries = useCallback(
    (fromIndex: number, toIndex: number) => {
      updateDraft((previous) => ({
        ...previous,
        series: toMoved(previous.series, fromIndex, toIndex),
      }));
    },
    [updateDraft],
  );

  const handleUpdateSeriesLabel = useCallback(
    (parameterId: string, label: string) => {
      label = label.trim();
      if (!label) {
        const parameter = parameters.find((p) => p.id === parameterId);
        label = parameter ? parameter.label : "";
      }
      if (label) {
        setConfig((previous) => ({
          ...previous,
          series: previous.series.map((s) =>
            s.parameter_id === parameterId ? { ...s, label } : s,
          ),
        }));
      }
    },
    [parameters],
  );

  const handleDone = useCallback(() => {
    const finalized = finalizeTimeSeriesConfiguration(
      config,
      // Header-owned titles only fall back to the property kind label; the
      // optional inline title may also become the untitled label.
      editableTitle ? propertyKindTitle : (propertyKindTitle ?? untitledLabel),
    );
    if (!finalized) return;
    const result = onDone({
      ...finalized,
      visualization_type: coerceDraftVisualization(finalized),
    });
    if (!result) return;
    setSaving(true);
    // Both branches clear the state; the failure toast comes from the global
    // mutation error handler, so the user can retry on the re-enabled button.
    result.then(
      () => setSaving(false),
      () => setSaving(false),
    );
  }, [config, editableTitle, onDone, propertyKindTitle, untitledLabel]);

  const selectedParameter = useMemo(
    () => parameters.find((p) => p.id === selectedParameterId),
    [parameters, selectedParameterId],
  );

  const previewTitle = propertyKindTitle ?? untitledLabel;
  const livePreviewConfig = useMemo(
    () => toPreviewTimeSeriesConfiguration(config, previewTitle),
    [config, previewTitle],
  );
  const previewRender = renderPreview ? renderPreview(livePreviewConfig) : undefined;
  const previewContent = previewRender?.content ?? previewSlot;
  const previewToolbar = previewRender?.toolbar;
  const previewWrap = previewRender?.wrap ?? ((node: ReactNode) => node);
  const hasPreview = !!previewContent;
  const showPreviewColumn = hasPreview && isPreviewDesktop;
  const showPreviewButton = hasPreview && !isPreviewDesktop;

  const showUnitSelect = config.series.length > 0 && overlappingUnits.length > 1;
  const showPresentationSelect =
    config.series.length > 0 && allowedPresentationTypes.length > 0;

  const generalSection = (
    <div className={modalStyles.settingsField}>
      {unitsError && (
        <Alert severity="error">
          {t("admin.widgets.fields.model_map.time_series.property_kinds_error")}
        </Alert>
      )}
      {!editableTitle && (
        <>
          <label className={modalStyles.settingsLabel}>
            {t("admin.widgets.fields.model_map.time_series.title_label")}
          </label>
          <input
            type="text"
            value={config.title}
            onChange={(e) => setConfig((prev) => ({ ...prev, title: e.target.value }))}
            placeholder={
              propertyKindTitle ??
              t("admin.widgets.fields.model_map.time_series.title_placeholder")
            }
            className={modalStyles.titleInput}
          />
        </>
      )}
      {(showUnitSelect || showPresentationSelect) && (
        <div className={styles.generalControlsRow}>
          {showUnitSelect && (
            <div className={styles.nestedField}>
              <Select
                label={t("admin.widgets.fields.model_map.time_series.unit_label")}
                collection={unitCollection}
                value={selectedUnitCode ?? undefined}
                onValueChange={(value) => {
                  const unit = overlappingUnits.find(
                    (candidate) => candidate.code === value,
                  );
                  if (!unit) return;
                  setConfig((previous) => ({
                    ...previous,
                    unit,
                    visualization_type: coerceVisualizationType(
                      readVisualizationType(previous as ParameterTimeSeriesConfiguration),
                      unit.code,
                      seriesParameterIds(previous),
                    ),
                  }));
                }}
                name="time-series-unit"
              />
              <p className={modalStyles.fieldHelper}>
                {t("admin.widgets.fields.model_map.time_series.unit_picker_helper")}
              </p>
            </div>
          )}
          {showPresentationSelect && (
            <div className={styles.nestedField}>
              <Select
                label={t("admin.widgets.fields.model_map.time_series.presentation_label")}
                collection={presentationCollection}
                value={presentationValue}
                disabled={allowedPresentationTypes.length <= 1}
                onValueChange={(v) =>
                  setConfig((prev) => ({
                    ...prev,
                    visualization_type: v as ModelMapVisualizationType,
                  }))
                }
                name="time-series-presentation"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const parametersSection = (
    <div className={modalStyles.settingsField}>
      <label className={modalStyles.settingsLabel}>
        {t("admin.widgets.fields.model_map.time_series.parameters_in_config")}
      </label>
      {config.series.length > 0 ? (
        <SortableList
          onReorder={handleReorderSeries}
          className={modalStyles.seriesList}
          groupId="configurator-series"
        >
          {config.series.map((series, index) => {
            const param = parameters.find((p) => p.id === series.parameter_id);
            return (
              <SortableItem key={series.parameter_id} index={index}>
                <TimeSeriesSeriesCard
                  series={series}
                  parameter={param}
                  onRemove={() => handleRemoveSeries(series.parameter_id)}
                  onLabelChange={(label) =>
                    handleUpdateSeriesLabel(series.parameter_id, label)
                  }
                />
              </SortableItem>
            );
          })}
        </SortableList>
      ) : (
        <p className={modalStyles.modalEmpty}>
          {t("admin.widgets.fields.model_map.time_series.empty_series")}
        </p>
      )}
      <PickListModal
        title={t("admin.widgets.fields.model_map.parameters.add")}
        description={t("admin.widgets.fields.model_map.parameters.add_description", {
          model: "",
        })}
        addText={t("admin.widgets.fields.model_map.time_series.add_parameter")}
        emptyText={t("admin.widgets.fields.model_map.parameters.no_parameters_available")}
        items={pickListItems}
        datasetLabels={datasetLabels}
        selected={selectedParameterIds}
        onPick={handleAddParameter}
        loading={parametersLoading}
        checkedItemStatusLabel={{
          text: t("admin.widgets.fields.model_map.map_layers.already_added"),
          icon: "check",
        }}
      />
    </div>
  );

  // Thresholds only make sense on line graphs (bands/lines painted on the chart);
  // other presentations (scatter, histogram, arrows) cannot render them.
  const thresholdsSupportedByChart = visualizationSupportsThresholds(presentationValue);

  const thresholdsSection = !thresholdsAvailable ? (
    <p className={modalStyles.modalEmpty}>
      {t("admin.widgets.fields.model_map.time_series.thresholds_requires_parameters")}
    </p>
  ) : !thresholdsSupportedByChart ? (
    <p className={modalStyles.modalEmpty}>
      {t("admin.widgets.fields.model_map.time_series.thresholds_line_graph_only")}
    </p>
  ) : (
    <div className={modalStyles.settingsField}>
      <label className={modalStyles.settingsLabel}>
        {t("admin.widgets.fields.model_map.time_series.thresholds.section_label")}
      </label>
      <p className={modalStyles.fieldHelper}>
        {t("admin.widgets.fields.model_map.time_series.thresholds.section_helper")}
      </p>
      <TimeSeriesThresholdsEditor
        thresholds={config.thresholds}
        unitSymbol={config.unit?.symbol ?? undefined}
        onChange={(thresholds) => setConfig((previous) => ({ ...previous, thresholds }))}
      />
    </div>
  );

  const tabs: {
    value: BodyTab;
    label: string;
    icon: IconNames;
    content: ReactNode;
    struck?: boolean;
  }[] = [
    {
      value: BodyTab.TimeSeries,
      label: t("admin.widgets.fields.model_map.time_series.configurator_tabs.time_series"),
      icon: "list",
      content: (
        <div className={styles.tabSectionStack}>
          {generalSection}
          {parametersSection}
        </div>
      ),
    },
    {
      value: BodyTab.Thresholds,
      label: t("admin.widgets.fields.model_map.time_series.configurator_tabs.thresholds"),
      icon: "gauge",
      content: thresholdsSection,
      // Struck through when the current presentation cannot carry thresholds.
      struck: thresholdsAvailable && !thresholdsSupportedByChart,
    },
  ];
  const activeContent =
    tabs.find((tab) => tab.value === activeTab)?.content ?? tabs[0].content;

  const editableTitleStrings: MapWidgetEditableTitleStrings = {
    untitled: t("admin.widgets.fields.model_map.general.untitled"),
    // Labels the header field, which names the widget or group, so it reads as
    // the same "Naam" field the other widget forms show.
    nameLabel: t("admin.widgets.fields.name.label"),
    confirmTitleAria: t("admin.widgets.fields.model_map.general.confirm_title"),
    cancelEditingAria: t("admin.widgets.fields.model_map.general.cancel_editing"),
    editTitleAria: t("admin.widgets.fields.model_map.general.edit_title"),
  };

  const header = (
    <div className={styles.header}>
      {showBackButton && (
        <button
          type="button"
          className={styles.backButton}
          onClick={onCancel}
          aria-label={t("admin.widgets.fields.model_map.time_series.back_to_list")}
        >
          <Icon name="arrow-left" size={20} />
        </button>
      )}
      {editableTitle ? (
        <MapWidgetEditableTitle
          value={config.title}
          onChange={(title) => setConfig((prev) => ({ ...prev, title }))}
          strings={editableTitleStrings}
          fallback={propertyKindTitle}
          variant="auto"
        />
      ) : (
        <h2 className={styles.title}>
          {config.series.length > 0
            ? t("admin.widgets.fields.model_map.time_series.configurator_title")
            : t("admin.widgets.fields.model_map.time_series.configurator_title_create")}
        </h2>
      )}
      {showPreviewButton && (
        <Button
          type="button"
          variant="card"
          className={styles.previewButton}
          onClick={() => setPreviewModalOpen(true)}
        >
          <Stack gap={8} alignItems="center">
            <Eye size={16} />
            <span>{t("admin.widgets.fields.map_editor.preview.button")}</span>
          </Stack>
        </Button>
      )}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <Stack justifyContent="flex-end" gap={8}>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {cancelLabel ?? t("general.cancel")}
        </Button>
        <Button
          type="button"
          variant="solid"
          onClick={handleDone}
          disabled={!canDone}
          loading={saving}
        >
          {doneLabel ?? t("general.done")}
        </Button>
      </Stack>
    </div>
  );

  const contentRegion = (
    <div className={classnames(styles.paneled, styles.paneledHorizontal)}>
      <div className={classnames(styles.rail, styles.railHorizontal)} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.value}
            data-selected={activeTab === tab.value ? "" : undefined}
            className={styles.railButton}
            onClick={() => setActiveTab(tab.value)}
          >
            <Icon name={tab.icon} size={16} />
            <span className={classnames(tab.struck && styles.railLabelStruck)}>
              {tab.label}
            </span>
          </button>
        ))}
      </div>
      <div className={styles.content}>{activeContent}</div>
    </div>
  );

  return (
    <div className={classnames(styles.root, className)}>
      {header}
      {showPreviewColumn ? (
        <div className={styles.bodyWithPreview}>
          <div className={styles.mainColumn}>{contentRegion}</div>
          <div className={styles.previewColumn}>
            {previewWrap(
              <>
                <div className={styles.previewHeader}>
                  <span className={styles.previewTitle}>
                    {t("admin.widgets.fields.map_editor.preview.title")}
                  </span>
                  <div className={styles.previewHeaderActions}>
                    {previewToolbar}
                    <Button
                      type="button"
                      variant="ghost"
                      className={styles.previewExpandButton}
                      onClick={() => setPreviewModalOpen(true)}
                    >
                      <Icon name="maximize-2" size={16} />
                    </Button>
                  </div>
                </div>
                <div className={styles.previewBody}>{previewContent}</div>
              </>,
            )}
          </div>
        </div>
      ) : (
        contentRegion
      )}
      {footer}
      <ConfigureSeriesModal
        key={selectedParameterId ?? "none"}
        open={showConfigureSeries}
        parameter={selectedParameter}
        onSubmit={handleConfigureSeriesSubmit}
        onCancel={handleConfigureSeriesCancel}
      />
      {hasPreview &&
        previewWrap(
          <PreviewModal
            previewNode={previewContent}
            toolbar={previewToolbar}
            open={previewModalOpen}
            onOpenChange={setPreviewModalOpen}
            title={config.title.trim() || untitledLabel}
          />,
        )}
    </div>
  );
}
